import type { Point } from "../types";

export type ObjectTransformAction = "move" | "resize" | "rotate" | "duplicate" | "delete";

export type ObjectTransformShortcutCommand =
  | "select"
  | "deselect"
  | "select-all"
  | "cut"
  | "copy"
  | "paste"
  | "duplicate"
  | "merge"
  | "flip-horizontal"
  | "flip-vertical"
  | "delete"
  | "rotate-left"
  | "rotate-right"
  | "group"
  | "ungroup"
  | "send-backward"
  | "bring-forward"
  | "send-to-back"
  | "bring-to-front"
  | "nudge-left"
  | "nudge-right"
  | "nudge-up"
  | "nudge-down"
  | "undo"
  | "redo";

export type ObjectTransformContextCommand = Extract<ObjectTransformShortcutCommand,
  "duplicate" | "merge" | "flip-horizontal" | "flip-vertical" | "rotate-left" | "rotate-right" | "group" | "ungroup" | "send-backward" | "bring-forward" | "send-to-back" | "bring-to-front" | "delete"
>;

export interface ObjectTransformBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ObjectTransformAlignment = "left" | "center-horizontal" | "right" | "top" | "center-vertical" | "bottom" | "distribute-horizontal" | "distribute-vertical";

/**
 * Returns aligned or evenly distributed bounds without knowing which editor
 * owns the objects. Geometry editors can apply the returned bounds through
 * their normal transform pipeline, keeping snapping, undo, and export in one
 * place.
 */
export function alignObjectTransformBounds(bounds: readonly ObjectTransformBounds[], alignment: ObjectTransformAlignment): ObjectTransformBounds[] {
  if (bounds.length < 2) return bounds.map((item) => ({ ...item }));
  if (alignment === "distribute-horizontal" || alignment === "distribute-vertical") {
    const axis = alignment === "distribute-horizontal" ? "x" : "y";
    const size = alignment === "distribute-horizontal" ? "width" : "height";
    const ordered = bounds.map((item, index) => ({ item, index })).sort((a, b) => a.item[axis] - b.item[axis]);
    if (ordered.length < 3) return bounds.map((item) => ({ ...item }));
    const first = ordered[0].item[axis]; const last = ordered[ordered.length - 1].item[axis];
    const firstCenter = first + ordered[0].item[size] / 2; const lastCenter = last + ordered[ordered.length - 1].item[size] / 2;
    const result = bounds.map((item) => ({ ...item }));
    ordered.slice(1, -1).forEach(({ item, index }, position) => {
      const center = firstCenter + (lastCenter - firstCenter) * ((position + 1) / (ordered.length - 1));
      result[index] = axis === "x" ? { ...result[index], x: center - item.width / 2 } : { ...result[index], y: center - item.height / 2 };
    });
    return result;
  }
  const left = Math.min(...bounds.map((item) => item.x)); const right = Math.max(...bounds.map((item) => item.x + item.width));
  const top = Math.min(...bounds.map((item) => item.y)); const bottom = Math.max(...bounds.map((item) => item.y + item.height));
  const centerX = (left + right) / 2; const centerY = (top + bottom) / 2;
  return bounds.map((item) => {
    if (alignment === "left") return { ...item, x: left };
    if (alignment === "center-horizontal") return { ...item, x: centerX - item.width / 2 };
    if (alignment === "right") return { ...item, x: right - item.width };
    if (alignment === "top") return { ...item, y: top };
    if (alignment === "center-vertical") return { ...item, y: centerY - item.height / 2 };
    return { ...item, y: bottom - item.height };
  });
}

export const OBJECT_TRANSFORM_ACTIONS: ReadonlyArray<{ action: ObjectTransformAction; glyph: string; label: string }> = [
  { action: "move", glyph: "✥", label: "Move" },
  { action: "resize", glyph: "↘", label: "Resize" },
  { action: "rotate", glyph: "↻", label: "Rotate" },
  { action: "duplicate", glyph: "⧉", label: "Duplicate" },
  { action: "delete", glyph: "×", label: "Delete" },
];

/** Compact object-local actions shared by every transform-capable editor. */
export const OBJECT_TRANSFORM_CONTEXT_ACTIONS: ReadonlyArray<{ command: ObjectTransformContextCommand; glyph: string; label: string }> = [
  { command: "duplicate", glyph: "⧉", label: "Duplicate" },
  { command: "merge", glyph: "⊕", label: "Join strokes" },
  { command: "flip-horizontal", glyph: "⇋", label: "Flip horizontally" },
  { command: "flip-vertical", glyph: "⇵", label: "Flip vertically" },
  { command: "rotate-left", glyph: "↺", label: "Rotate left 15°" },
  { command: "rotate-right", glyph: "↻", label: "Rotate right 15°" },
  { command: "group", glyph: "◇", label: "Group" },
  { command: "ungroup", glyph: "◇̸", label: "Ungroup" },
  { command: "send-to-back", glyph: "⇊", label: "Send to back" },
  { command: "send-backward", glyph: "↓", label: "Send backward" },
  { command: "bring-forward", glyph: "↑", label: "Bring forward" },
  { command: "bring-to-front", glyph: "⇈", label: "Bring to front" },
  { command: "delete", glyph: "×", label: "Delete" },
];

/** Keep the shared object command palette usable on narrow map viewports. */
export function objectTransformToolbarLayout(commandCount: number, maxColumns = 7) {
  const count = Math.max(0, Math.floor(commandCount));
  const columns = count === 0 ? 0 : Math.min(Math.max(1, Math.floor(maxColumns)), count);
  return { columns, rows: columns ? Math.ceil(count / columns) : 0 };
}

/**
 * One shortcut vocabulary for every TBLTOP object editor. Components render this
 * list and resolve keyboard events through the same contract so documentation
 * and behavior cannot drift apart.
 */
export const OBJECT_TRANSFORM_SHORTCUTS: ReadonlyArray<{ command: ObjectTransformShortcutCommand; keys: string; label: string }> = [
  { command: "select", keys: "V", label: "Select" },
  { command: "deselect", keys: "Esc", label: "Deselect" },
  { command: "select-all", keys: "⌘/Ctrl A", label: "Select all" },
  { command: "cut", keys: "⌘/Ctrl X", label: "Cut" },
  { command: "copy", keys: "⌘/Ctrl C", label: "Copy" },
  { command: "paste", keys: "⌘/Ctrl V", label: "Paste" },
  { command: "duplicate", keys: "⌘/Ctrl D", label: "Duplicate" },
  { command: "merge", keys: "⌘/Ctrl J", label: "Join strokes" },
  { command: "flip-horizontal", keys: "⌘/Ctrl Shift H", label: "Flip horizontal" },
  { command: "flip-vertical", keys: "⌘/Ctrl Shift V", label: "Flip vertical" },
  { command: "nudge-right", keys: "↑↓←→", label: "Nudge" },
  { command: "rotate-right", keys: "[ ]", label: "Rotate 15°" },
  { command: "group", keys: "⌘/Ctrl G", label: "Group" },
  { command: "ungroup", keys: "⇧⌘/Ctrl G", label: "Ungroup" },
  { command: "send-backward", keys: "⌘/Ctrl [", label: "Send backward" },
  { command: "bring-forward", keys: "⌘/Ctrl ]", label: "Bring forward" },
  { command: "send-to-back", keys: "⇧⌘/Ctrl [", label: "Send to back" },
  { command: "bring-to-front", keys: "⇧⌘/Ctrl ]", label: "Bring to front" },
  { command: "delete", keys: "Delete", label: "Remove" },
  { command: "undo", keys: "⌘/Ctrl Z", label: "Undo" },
  { command: "redo", keys: "⇧⌘/Ctrl Z", label: "Redo" },
];

/**
 * Machine-readable shortcut metadata for object-local controls.  Editors use
 * the same vocabulary for keyboard handling, legends, and accessible tooltips
 * so a command can never advertise a different shortcut in another editor.
 */
export function objectTransformShortcutKeys(command: ObjectTransformShortcutCommand | ObjectTransformAction): string | undefined {
  const keys = OBJECT_TRANSFORM_SHORTCUTS.find((item) => item.command === command)?.keys;
  if (!keys) return undefined;
  return keys
    .replaceAll("⇧⌘/Ctrl", "Shift+Meta+Control")
    .replaceAll("⌘/Ctrl", "Meta+Control")
    .replaceAll("Shift+Meta+Control ", "Shift+Meta+Control+")
    .replaceAll("Meta+Control ", "Meta+Control+")
    .replaceAll("↑↓←→", "Arrow keys")
    .replaceAll("Esc", "Escape");
}

export function resolveObjectTransformShortcut(event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey">): ObjectTransformShortcutCommand | undefined {
  const modifier = event.metaKey || event.ctrlKey; const key = event.key.toLowerCase();
  if (event.key === "Escape") return "deselect";
  if (modifier && key === "z") return event.shiftKey ? "redo" : "undo";
  if (modifier && key === "a") return "select-all";
  if (modifier && key === "x") return "cut";
  if (modifier && key === "c") return "copy";
  if (modifier && event.shiftKey && key === "v") return "flip-vertical";
  if (modifier && key === "v") return "paste";
  if (modifier && key === "d") return "duplicate";
  if (modifier && key === "j") return "merge";
  if (modifier && event.shiftKey && key === "h") return "flip-horizontal";
  if (modifier && key === "g") return event.shiftKey ? "ungroup" : "group";
  if (modifier && (event.key === "[" || event.key === "{")) return event.shiftKey ? "send-to-back" : "send-backward";
  if (modifier && (event.key === "]" || event.key === "}")) return event.shiftKey ? "bring-to-front" : "bring-forward";
  if (!modifier && key === "v") return "select";
  if (event.key === "Delete" || event.key === "Backspace") return "delete";
  if (event.key === "[") return "rotate-left";
  if (event.key === "]") return "rotate-right";
  if (event.key === "ArrowLeft") return "nudge-left";
  if (event.key === "ArrowRight") return "nudge-right";
  if (event.key === "ArrowUp") return "nudge-up";
  if (event.key === "ArrowDown") return "nudge-down";
  return undefined;
}

export const objectTransformCenter = (bounds: ObjectTransformBounds): Point => ({ x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 });

export function objectTransformHandlePoint(bounds: ObjectTransformBounds, action: ObjectTransformAction, offset = .8): Point {
  const center = objectTransformCenter(bounds);
  if (action === "move") return center;
  if (action === "resize") return { x: bounds.x + bounds.width, y: bounds.y + bounds.height };
  if (action === "rotate") return { x: center.x, y: bounds.y - offset };
  if (action === "duplicate") return { x: bounds.x, y: bounds.y - offset };
  return { x: bounds.x + bounds.width, y: bounds.y - offset };
}

export function resizeObjectTransformBounds(origin: ObjectTransformBounds, point: Point, keepProportions = false, minimum = .25): ObjectTransformBounds {
  let width = Math.max(minimum, point.x - origin.x); let height = Math.max(minimum, point.y - origin.y);
  if (keepProportions) {
    const ratio = Math.max(minimum, origin.width) / Math.max(minimum, origin.height); const widthFromHeight = height * ratio;
    if (widthFromHeight <= width) height = width / ratio; else width = widthFromHeight;
  }
  return { ...origin, width, height };
}

export function objectTransformRotation(bounds: ObjectTransformBounds, point: Point, snapDegrees = 0) {
  const center = objectTransformCenter(bounds); let degrees = Math.atan2(point.y - center.y, point.x - center.x) * 180 / Math.PI + 90;
  if (snapDegrees > 0) degrees = Math.round(degrees / snapDegrees) * snapDegrees;
  return degrees;
}

export function transformObjectPoint(point: Point, origin: ObjectTransformBounds, next: ObjectTransformBounds, rotationDegrees = 0): Point {
  const originCenter = objectTransformCenter(origin); const nextCenter = objectTransformCenter(next); const scaleX = next.width / Math.max(.0001, origin.width); const scaleY = next.height / Math.max(.0001, origin.height); const x = (point.x - originCenter.x) * scaleX; const y = (point.y - originCenter.y) * scaleY; const radians = rotationDegrees * Math.PI / 180;
  return { x: nextCenter.x + x * Math.cos(radians) - y * Math.sin(radians), y: nextCenter.y + x * Math.sin(radians) + y * Math.cos(radians) };
}

export function objectBoundsFromPoints(points: readonly Point[], fallback: ObjectTransformBounds = { x: 0, y: 0, width: 1, height: 1 }): ObjectTransformBounds {
  if (!points.length) return fallback; const xs = points.map((point) => point.x); const ys = points.map((point) => point.y); const x = Math.min(...xs); const y = Math.min(...ys);
  return { x, y, width: Math.max(.25, Math.max(...xs) - x), height: Math.max(.25, Math.max(...ys) - y) };
}
